import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, Mail, Lock, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { trackSignUp } from "@/lib/analytics";
import { submitPendingQuiz, getPendingQuiz } from "@/lib/pendingQuiz";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, GoogleButton } from "@/pages/Login";

export default function Register() {
  const { register, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const hasPending = !!getPendingQuiz();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await register(name, email, password);
      trackSignUp("email");
      const pending = await submitPendingQuiz();
      if (pending) {
        await checkAuth();
        navigate((u.role === "admin" || u.is_subscribed) ? "/dashboard" : "/subscribe", { replace: true });
      } else {
        navigate("/quiz", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title={hasPending ? "Almost there — save your path" : "Start your AI path"}
      subtitle={hasPending ? "Create your account to unlock the personalized roadmap we just built for you." : "Create your account — it takes 60 seconds."}
    >
      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
        <div className="space-y-1.5">
          <Label htmlFor="name">Your name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input id="name" data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Aarav" className="pl-10 h-11 rounded-lg border border-border focus-visible:ring-2 focus-visible:ring-primary/40" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email">Email</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input id="email" data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 h-11 rounded-lg border border-border focus-visible:ring-2 focus-visible:ring-primary/40" />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input id="password" data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="pl-10 h-11 rounded-lg border border-border focus-visible:ring-2 focus-visible:ring-primary/40" />
          </div>
        </div>
        {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
        <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full h-11 bg-primary text-primary-foreground">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account & build my path"}
        </Button>
      </form>

      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="flex-1 h-px bg-border" /> or <div className="flex-1 h-px bg-border" />
      </div>
      <GoogleButton label="Sign up with Google" />

      <p className="mt-6 text-sm text-muted-foreground text-center">
        Already have an account?{" "}
        <Link to="/login" data-testid="to-login" className="text-primary font-medium hover:underline">Log in</Link>
      </p>
    </AuthShell>
  );
}
